import { createContext, useContext, useState, type PropsWithChildren } from 'react';
import type { I18n, LanguagePack, Locale, MessageValue, Messages, TranslationParams } from './types';
import { languagePacks } from './packs';

export type { I18n, LanguagePack, Locale, Messages, TranslationParams } from './types';
export { zhCN } from './locales/zh-CN';
export { languagePacks } from './packs';

type I18nOptions = {
  packs: Record<Locale, LanguagePack>;
  locale: Locale;
};

function messageAt(messages: Messages, key: string): MessageValue | undefined {
  return key.split('.').reduce<MessageValue | undefined>((value, segment) => {
    if (!value || typeof value === 'string') return undefined;
    return value[segment];
  }, messages);
}

function interpolate(message: string, params?: TranslationParams) {
  if (!params) return message;
  return message.replace(/\{(\w+)\}/g, (placeholder, key: string) => (
    key in params ? String(params[key]) : placeholder
  ));
}

export function createI18n({ packs, locale: initialLocale }: I18nOptions): I18n {
  let currentLocale = packs[initialLocale] ? initialLocale : Object.keys(packs)[0];
  const locales = Object.keys(packs);

  return {
    get locale() {
      return currentLocale;
    },
    locales,
    t(key, params) {
      const value = messageAt(packs[currentLocale].messages, key);
      const fallback = key.startsWith('ui.') ? key.slice(3) : key;
      return interpolate(typeof value === 'string' ? value : fallback, params);
    },
    setLocale(locale) {
      if (!packs[locale]) return false;
      currentLocale = locale;
      return true;
    },
  };
}

const I18nContext = createContext<I18n>(createI18n({ packs: languagePacks, locale: 'zh-CN' }));

const LOCALE_STORAGE_KEY = 'noflight-locale';

function storedLocale(packs: Record<Locale, LanguagePack>, fallback: Locale) {
  if (typeof window === 'undefined') return fallback;
  const saved = window.localStorage.getItem(LOCALE_STORAGE_KEY);
  return saved && packs[saved] ? saved : fallback;
}

export function setLocaleAndReload(locale: Locale) {
  if (typeof window === 'undefined' || !languagePacks[locale]) return false;
  window.localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  window.location.reload();
  return true;
}

export function I18nProvider({ packs, locale = 'zh-CN', children }: PropsWithChildren<I18nOptions>) {
  const [i18n] = useState(() => createI18n({ packs, locale: storedLocale(packs, locale) }));

  return (
    <I18nContext.Provider value={{ ...i18n, setLocale: setLocaleAndReload }}>
      {children}
    </I18nContext.Provider>
  );
}

export function useI18n() {
  return useContext(I18nContext);
}
